import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Box, CircularProgress } from '@material-ui/core';
import TeamLogo from '../atoms/TeamLogo';
import {
  COLOR_BORDER,
  COLOR_ORANGE,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_MIDDLE_GRAY,
  COLOR_TEXT,
  COLOR_BACKGROUND_LIGHT,
  COLOR_YELLOW,
} from '../styles/colors';
import Player from '../types/Player';
import Team from '../types/Team';
import Dialog from './Dialog';
import FullGameLogTable, { GameLogRow } from './FullGameLogTable';
import { CollegeProductionRow } from '../types/CollegeProductionData';
import { PlayerGameLog } from '../types/PlayerGameLog';
import { formatDate } from '../services/player';

export interface CollegeYearEntry {
  year: number;
  team?: Team;
  teamLogo?: string;
  teamName: string;
  jerseyNumber?: string;
  starts: number;
  games: number;
  snaps: number;
  mostSnaps?: boolean;
}

interface CollegeCareerOverviewProps {
  className?: string;
  player?: Player;
  fullGameLogHref?: string;
  collegeProductionData: CollegeProductionRow[];
  playerSSAGameLogData?: PlayerGameLog[] | null;
  isPlayerSSAGameLogLoading?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: theme.spacing(1),
    WebkitOverflowScrolling: 'touch',

    '&::-webkit-scrollbar': {
      height: 8,
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: COLOR_MIDDLE_GRAY,
      borderRadius: 4,
    },
  },
  scrollRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    minWidth: 'min-content',
    padding: theme.spacing(2, 0),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 0),
    },
  },
  yearCardWrapper: {
    position: 'relative',
    display: 'flex',
  },
  mostSnapsBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 84,
    minHeight: 22,
    padding: theme.spacing(0.5, 1),
    textAlign: 'center',
    borderRadius: '0 10px 0 5px',
    boxShadow: `0 1px 4px ${COLOR_BORDER}`,
    backgroundColor: '#FDD023',
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.1,
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  card: {
    boxSizing: 'border-box',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    width: 335,
    backgroundColor: COLOR_BACKGROUND_LIGHT,
    borderRadius: 10,
    border: `2px solid ${COLOR_BORDER}`,

    [theme.breakpoints.down('xs')]: {
      width: 280,
    },
  },
  summaryCard: {
    border: `2px solid ${COLOR_ORANGE}`,
  },
  cardHeader: {
    flexGrow: 1,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 70,
    padding: theme.spacing(2.5, 2),
    lineHeight: 1.1,

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(24),
    lineHeight: 1.2,
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  yearHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1, 4),

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  yearBadge: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  teamLogo: {
    height: 24,
    width: 'auto',
  },
  teamLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  fullGameLogLink: {
    color: COLOR_BLUE,
    textDecoration: 'underline',
    fontWeight: 700,
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  cardBody: {
    display: 'flex',
    alignItems: 'stretch',
    marginTop: 'auto',
    borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
  },
  statColumn: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(.5),
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
    padding: theme.spacing(.5),
    borderRight: `2px solid ${COLOR_MIDDLE_GRAY}`,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.2,

    '&:last-child': {
      borderRight: 'none',
    },

    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  statLabel: {
    color: COLOR_TEXT,
  },
  fullGameLogDialogContent: {
    overflow: 'auto',
    width: '96%',
    maxWidth: 1600,
    maxHeight: 'calc(100% - 40px)',
    borderRadius: 8,
  },
  gameLogLoaderWrapper: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameLogEmptyState: {
    minHeight: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(14),
  },
}));

export default function CollegeCareerOverview(props: CollegeCareerOverviewProps) {
  const {
    className,
    fullGameLogHref,
    collegeProductionData,
    playerSSAGameLogData,
    isPlayerSSAGameLogLoading = false,
  } = props;

  const [isFullGameLogDialogOpen, setIsFullGameLogDialogOpen] = useState<boolean>(false);
  const classes = useStyles();

  const playerGameLogData = playerSSAGameLogData || [];

  const fullGameLogRows: GameLogRow[] = useMemo(() => {
    if (!playerGameLogData || playerGameLogData.length === 0) return [];

    const sortedGameLogs = [...playerGameLogData].sort((a, b) => {
      const aTime = new Date(a.gameDate || a.createdAt || '').getTime();
      const bTime = new Date(b.gameDate || b.createdAt || '').getTime();
      const aIsValid = !Number.isNaN(aTime);
      const bIsValid = !Number.isNaN(bTime);

      if (aIsValid && bIsValid) {
        return aTime - bTime;
      }
      if (aIsValid) {
        return -1;
      }
      if (bIsValid) {
        return 1;
      }
      return 0;
    });
    
    return sortedGameLogs.map((row, idx) => {
      // const gameDate = row.gameDate ? new Date(row.gameDate) : null;
      // const formattedDate1 = gameDate && !Number.isNaN(gameDate.getTime())
      //   ? `${String(gameDate.getMonth() + 1).padStart(2, '0')}/${String(gameDate.getDate()).padStart(2, '0')}/${String(gameDate.getFullYear()).slice(-2)}`
      //   : '--/--/--';
      const formattedDate = formatDate(row.gameDate || null, true);
      const rushAtt = Number(row.rushAtt) || 0;
      const rushYards = Number(row.rushYard) || 0;
      const ypa = rushAtt > 0 ? Math.round((rushYards / rushAtt) * 10) / 10 : 0;

      const rec = Number(row.rec) || 0;
      const recYards = Number(row.recYards) || 0;
      const ypc = rec > 0 ? Math.round((recYards / rec) * 10) / 10 : 0;

      return {
        id: String(row.gameId || idx),
        date: formattedDate,
        isHomeTeam: row.isHomeTeam,
        opponent: row.opponentTeam?.name || 'Unknown',
        opponentHref: row?.opponentTeam?.name ? `/team/${row.opponentTeam.name.toLowerCase().replace(/\s+/g, '-')}` : undefined,
        score: (row.teamScore != null && row.opponentTeamScore != null) ? `${row.teamScore}-${row.opponentTeamScore} ${row.teamScore == row.opponentTeamScore ? 'D' : (row.teamScore > row.opponentTeamScore) ? 'W': "L" }` : '',
        passing: {
          comp: Number(row.passComp) || 0,
          att: Number(row.passAtt) || 0,
          ints: Number(row.passInt) || 0,
          yds: Number(row.passYard) || 0,
          tds: Number(row.passTD) || 0,
        },
        rushing: {
          att: rushAtt,
          yds: rushYards,
          td: Number(row.rushTD) || 0,
          ypa,
        },
        receiving: {
          rec: Number(row.rec) || 0,
          yds: Number(row.recYards) || 0,
          tds: Number(row.recTD) || 0,
          ypc,
        },
        defense: {
          tkls: Number(row.tackleSolo) || 0,
          sacks: Number(row.sack) || 0,
          tfls: Number(row.tackleForLoss) || 0,
          ints: Number(row.intRet) || 0,
          ffs: Number(row.fumbleForced) || 0,
          frs: Number(row.fumRet) || 0,
        },
      };
    });
  }, [playerGameLogData]);

  const collegeCareerOverviewBase: CollegeYearEntry[] = collegeProductionData.sort((a, b) => b.year - a.year).map((row, index) => ({
    year: row.year,
    teamLogo: row.logo247 || row.logoESPN,
    teamName: row.teamName,
    jerseyNumber: '45',//row?.jerseyNumber,
    starts: row?.gamesStarted,
    games: row?.gamesPlayed,
    snaps: row?.snaps,
    mostSnaps: false,
  }));

  const maxSnaps = collegeCareerOverviewBase.reduce(
    (maxValue, entry) => Math.max(maxValue, Number(entry.snaps) || 0),
    0,
  );

  const collegeCareerOverview: CollegeYearEntry[] = collegeCareerOverviewBase.map(entry => ({
    ...entry,
    mostSnaps: (Number(entry.snaps) || 0) === maxSnaps,
  }));

  const totals = collegeCareerOverview.length ? {
    starts: collegeCareerOverview.reduce((total, entry) => total + (Number(entry.starts) || 0), 0), 
    games: collegeCareerOverview.reduce((total, entry) => total + (Number(entry.games) || 0), 0),
    snaps: collegeCareerOverview.reduce((total, entry) => total + (Number(entry.snaps) || 0), 0),
  } : { starts: 0, games: 0, snaps: 0 };

  const years = collegeCareerOverview;

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.scrollRow}>
        {/* Summary card */}
        <div className={clsx(classes.card, classes.summaryCard)}>
          <div className={classes.cardHeader}>
            <span className={classes.cardTitle}>College Career</span>
            {
              playerGameLogData?.length ?
                fullGameLogHref != null ? (
                  <a
                    href={fullGameLogHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.fullGameLogLink}
                  >
                    Full Game Log
                  </a>
                ) : (
                  <span onClick={() => {setIsFullGameLogDialogOpen(true)}} className={classes.fullGameLogLink}>Full Game Log</span>
                )
              : null
            }
          </div>
          <div className={classes.cardBody}>
            <div className={classes.statColumn}>
              <span className={classes.statLabel}>
                <b>{totals.starts}</b> Starts
              </span>
            </div>
            <div className={classes.statColumn}>
              <span className={classes.statLabel}>
                <b>{totals.games}</b> Games
              </span>
            </div>
            <div className={classes.statColumn}>
              <span className={classes.statLabel}>
                <b>{totals.snaps}</b> Snaps
              </span>
            </div>
          </div>
        </div>

        {/* Year cards */}
        {years.map((entry, idx) => (
          <div key={idx} className={classes.yearCardWrapper}>
            {entry.mostSnaps && (
              <span className={classes.mostSnapsBadge}>Most Snaps</span>
            )}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.yearHeader}>
                  <span className={classes.yearBadge}>{entry.year}</span>
                  <span className={classes.teamLabel}>
                    {entry.teamLogo && (
                      <img src={entry.teamLogo} alt={`${entry.teamName} logo`} className={classes.teamLogo} />
                    )}
                    {entry.teamName}
                    {entry.jerseyNumber != null && entry.jerseyNumber !== '' && ` - #${entry.jerseyNumber}`}
                  </span>
                </div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.statColumn}>
                  <span className={classes.statLabel}>
                    <b>{entry.starts}</b> Starts
                  </span>
                </div>
                <div className={classes.statColumn}>
                  <span className={classes.statLabel}>
                    <b>{entry.games}</b> Games
                  </span>
                </div>
                <div className={classes.statColumn}>
                  <span className={classes.statLabel}>
                    <b>{entry.snaps}</b> Snaps
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <Dialog
        open={isFullGameLogDialogOpen}
        title="Full Game Log"
        onClose={() => setIsFullGameLogDialogOpen(false)}
        containsLongContent
        noContentPadding={true}
        contentClassName={classes.fullGameLogDialogContent}
      >
        {isPlayerSSAGameLogLoading ? (
          <Box className={classes.gameLogLoaderWrapper}>
            <CircularProgress size={28} />
          </Box>
        ) : fullGameLogRows.length > 0 ? (
          <FullGameLogTable useNamedBox={false} rows={fullGameLogRows} />
        ) : (
          <Box className={classes.gameLogEmptyState}>No game log data available.</Box>
        )}
      </Dialog>
    </div>
  );
}
